import { DragEvent, useRef, useState } from 'react'
import Button from '@/shared/Buttons/Button'
import DropArea from '@/shared/Files/Droparea'
import Image from '@/shared/Common/Image'
import ThreeDotsLoader from '@/shared/Loaders/ThreeDotsLoader'
import alertService from '@/services/alerts/alert.service'
import confirmService from '@/services/ConfirmService/confirm.service'
import TrashIcon from '@/assets/icons/trash.svg?react'
import UploadIcon from '@/assets/icons/upload.svg?react'
import CheckIcon from '@/assets/icons/checked.svg?react'
import cmsApi from '../cms.api'
import { ICmsAsset } from '../cms.types'
import { formatFileSize, getAbsoluteAssetUrl } from '../cms.utils'
import css from '../cms.module.css'

interface IProps {
  assets: ICmsAsset[]
  loading: boolean
  selectedUrl?: string
  selectMode?: boolean
  onSelect?: (asset: ICmsAsset) => void
  onRefresh: () => Promise<void>
}

const AssetManager = ({ assets, loading, selectedUrl, selectMode, onSelect, onRefresh }: IProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [dragActive, setDragActive] = useState(false)

  const upload = async (file?: File) => {
    if (!file) return

    if (!file.type.startsWith('image/')) {
      alertService.requestAlert({ type: 'warning', title: 'Файл не загружен', description: 'Можно загрузить только изображение' })
      return
    }

    setUploading(true)
    const response = await cmsApi.uploadAsset(file)
    setUploading(false)

    if (response.status === 'error') {
      alertService.requestAlert({ type: 'error', title: 'Не удалось загрузить файл', description: response.message })
      return
    }

    alertService.requestAlert({ type: 'success', title: 'Файл загружен', description: response.body.fileName })
    await onRefresh()
  }

  const handleDelete = async (asset: ICmsAsset) => {
    const confirmation = await confirmService.requestConfirm({
      title: 'Удалить файл',
      description: asset.fileName,
      confirmText: 'Удалить',
      isDanger: true,
    })

    if (confirmation.response !== 'confirm') return

    const response = await cmsApi.deleteAsset(asset.id)

    if (response.status === 'error') {
      alertService.requestAlert({ type: 'error', title: 'Не удалось удалить файл', description: response.message })
      return
    }

    alertService.requestAlert({ type: 'success', title: 'Файл удален', description: asset.fileName })
    await onRefresh()
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragActive(false)
    upload(event.dataTransfer.files?.[0])
  }

  return (
    <DropArea
      isActive={dragActive}
      onDrop={handleDrop}
      onDragEnter={(event) => {
        event.preventDefault()
        setDragActive(true)
      }}
      onDragLeave={() => setDragActive(false)}
      onDragOver={(event) => event.preventDefault()}
      style={{ height: '100%' }}
    >
      <div className={`flex-lines gap16 ${css.dropSurface}`}>
        <div className="flex-space-between">
          <div>
            <h2 className="text-heading-5">Медиафайлы</h2>
            <p className="text-nm text-description">{assets.length} изображений</p>
          </div>
          <div className="inline-flex-gap gap8 center">
            {(loading || uploading) && <ThreeDotsLoader />}
            <Button
              size="default"
              type="primary"
              text="Загрузить"
              icon={<UploadIcon />}
              loading={uploading}
              onClick={() => inputRef.current?.click()}
            />
          </div>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(event) => upload(event.target.files?.[0])}
        />

        {assets.length === 0 && !loading ? (
          <div className={css.emptyState}>
            <p className="text-nm text-description">Нет загруженных файлов</p>
          </div>
        ) : (
          <div className={css.assetGrid}>
            {assets.map((asset) => (
              <article key={asset.id} className={css.assetCard} data-active={asset.url === selectedUrl}>
                <Image
                  alt={asset.fileName}
                  src={getAbsoluteAssetUrl(asset.url)}
                  wrapperClassname={css.assetImage}
                  style={{ width: '100%', height: '100%' }}
                />
                <div className={css.assetMeta}>
                  <p className="text-nm-bold line-clamp" title={asset.fileName}>
                    {asset.fileName}
                  </p>
                  <p className="text-sm text-description">
                    {asset.extension || asset.dataType} · {formatFileSize(asset.size)}
                  </p>
                  <div className="inline-flex-gap gap8 center">
                    {selectMode && (
                      <Button
                        size="small"
                        type={asset.url === selectedUrl ? 'primary' : 'default'}
                        text={asset.url === selectedUrl ? 'Выбран' : 'Выбрать'}
                        icon={asset.url === selectedUrl ? <CheckIcon /> : undefined}
                        onClick={() => onSelect?.(asset)}
                      />
                    )}
                    <Button
                      size="small"
                      type="default"
                      danger
                      icon={<TrashIcon />}
                      onClick={() => handleDelete(asset)}
                    />
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </DropArea>
  )
}

export default AssetManager
